import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { localStorageGet, localStorageSet } from '../utils/localStorage';

type LanguageContextType = {
  currentLanguage: string;
  preferredLanguage: string;
  changeLanguage: (lang: string) => void;
  setPreferredLanguage: (lang: string) => void;
  availableLanguages: { code: string; name: string }[];
};

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const LanguageProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const initialLanguage = localStorageGet('i18nextLng', 'en') as string;
  const [preferredLanguage, setPreferredLanguageState] =
    useState<string>(initialLanguage);
  const [currentLanguage, setCurrentLanguage] =
    useState<string>(initialLanguage);

  const availableLanguages = [
    { code: 'en', name: 'English' },
    { code: 'hi', name: 'हिंदी' },
    { code: 'ka', name: 'ಕನ್ನಡ' },
  ];

  // Initialize i18n on mount
  useEffect(() => {
    const savedLanguage = localStorageGet('i18nextLng', 'en') as string;
    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  // Sync preferredLanguage changes with i18n and localStorage
  useEffect(() => {
    if (preferredLanguage && preferredLanguage !== i18n.language) {
      i18n.changeLanguage(preferredLanguage);
      setCurrentLanguage(preferredLanguage);
      localStorageSet('i18nextLng', preferredLanguage);
    }
  }, [preferredLanguage, i18n]);

  // Keep currentLanguage in sync with i18n
  useEffect(() => {
    if (i18n.language && i18n.language !== currentLanguage) {
      setCurrentLanguage(i18n.language);
    }
  }, [i18n.language, currentLanguage]);

  const changeLanguage = (lang: string) => {
    setPreferredLanguageState(lang);
  };

  const setPreferredLanguage = (lang: string) => {
    setPreferredLanguageState(lang);
  };

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        preferredLanguage,
        changeLanguage,
        setPreferredLanguage,
        availableLanguages,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
